import { useState } from 'react';
import { Card, CardActions, CardContent, CardHeader, Fade, Paper, Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import Slider from '@mui/material/Slider';
import Percent from '@mui/icons-material/Percent';
import { SaveAlt } from '@mui/icons-material';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import MDButton from 'components/MDButton';
import { useComputesWhitelistMutation } from 'services/anonymizeApi';
import { useNotification } from 'hook/useNotification';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import 'filepond/dist/filepond.min.css';

registerPlugin(FilePondPluginFileValidateType);

export default function AnonymizeTools() {
  const [computesWhitelist, { isLoading }] = useComputesWhitelistMutation();

  const { formatMessage: msg } = useIntl();
  const [threshold, setThreshold] = useState(75);
  const [files, setFiles] = useState([]);

  const [resultsTitle, setResultsTitle] = useState('');
  const [results, setResults] = useState('');
  const [resultPayload, setResultPayload] = useState('');

  const minThreshold = 50;
  const maxThreshold = 100;

  const { addNotification } = useNotification();

  const handleThresholdChange = (event, newThreshold) => {
    setThreshold(newThreshold);
  };

  const processFiles = (type) => {
    setResultsTitle(msg({ id: 'page.anonymization.tools.whitelist.waiting' }));
    setResults('');
    setResultPayload(null);

    // Extract actual File objects from FilePond file objects
    const fileObjects = files.map((fileItem) => fileItem.file);
    computesWhitelist({ files: fileObjects, threshold, type })
      .unwrap()
      .then((payload) => {
        setResultPayload(payload);
        const terms = payload.terms.map((term) => term.entity).join(', ');
        if (terms.length > 0) {
          setResultsTitle(msg({ id: 'page.anonymization.tools.whitelist.results.title' }));
          setResults(terms);
        } else {
          setResultsTitle(msg({ id: 'page.anonymization.tools.whitelist.no.result' }));
          setResults('');
        }
      })
      .catch((error) => {
        setResultsTitle('');
        addNotification({
          type: 'error',
          title: msg({ id: 'page.anonymization.tools.whitelist.error.title' }),
          message:
            error.data?.message || msg({ id: 'page.anonymization.tools.whitelist.error.default' })
        });
      });
  };

  const handleDownloadTerms = () => {
    const terms = resultPayload.terms?.map((nerEntry) => nerEntry.entity);
    const blob = new Blob([JSON.stringify(terms, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'whitelist.json';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  };

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <Card sx={{ width: '100%' }}>
            <CardHeader
              title={msg({ id: 'page.anonymization.tools.whitelist.title' })}
              subheader={msg({ id: 'page.anonymization.tools.whitelist.description' })}
            />

            <CardContent>
              <Stack direction="column" spacing={2} sx={{ marginTop: 'auto !important' }}>
                <FilePond
                  files={files}
                  acceptedFileTypes={['application/pdf']}
                  onupdatefiles={setFiles}
                  allowMultiple
                  maxFiles={10}
                  name="fichiers"
                  labelIdle={msg({ id: 'file.upload.description' })}
                />

                <div>
                  <MDTypography id="threshold-slider" gutterBottom>
                    {msg({ id: 'page.anonymization.tools.whitelist.threshold' }, { threshold })}
                  </MDTypography>

                  <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                    <Percent />
                    <Slider
                      sx={{ width: '50%', ml: 1.5 }}
                      value={threshold}
                      aria-label={msg({ id: 'page.anonymization.tools.whitelist.threshold' })}
                      defaultValue={75}
                      marks
                      min={minThreshold}
                      max={maxThreshold}
                      onChange={handleThresholdChange}
                    />
                  </MDBox>
                </div>
              </Stack>
            </CardContent>
            <CardActions
              disableSpacing
              sx={{ display: 'flex', justifyContent: 'flex-end', columnGap: 1 }}
            >
              <MDButton
                size="small"
                color="primary"
                onClick={() => processFiles('fast')}
                type="submit"
                disabled={files.length === 0}
                loading={isLoading}
              >
                {msg({ id: 'action.analyse.fast' })}
              </MDButton>
              <MDButton
                size="small"
                color="primary"
                onClick={() => processFiles('full')}
                type="submit"
                disabled={files.length === 0}
                loading={isLoading}
              >
                {msg({ id: 'action.analyse.full' })}
              </MDButton>
            </CardActions>

            {resultsTitle?.length > 0 ? (
              <Paper elevation={2} sx={{ flex: 1, borderRadius: '10px', p: 2 }}>
                {results?.length > 0 ? (
                  <MDButton
                    sx={{ float: 'right' }}
                    color="primary"
                    iconOnly
                    onClick={handleDownloadTerms}
                  >
                    <SaveAlt />
                  </MDButton>
                ) : null}
                <MDTypography gutterBottom>{resultsTitle}</MDTypography>
                <MDTypography gutterBottom>{results}</MDTypography>
              </Paper>
            ) : null}
          </Card>
        </Paper>
      </MDBox>
    </Fade>
  );
}
